<template>
  <div class="container mt-4">
    <h1>🔘 SwitchField</h1>
    <p>
      A reusable Vue 3 switch (toggle) component with <code>v-model</code> support,
      customizable sizes, and accessibility-friendly labeling.
    </p>

    <hr />

    <h2>✨ Features</h2>
    <ul>
      <li>🔄 Two-way binding via <code>v-model</code></li>
      <li>🎚 Toggle boolean values (true / false)</li>
      <li>📏 Multiple sizes: <code>sm</code>, <code>md</code>, <code>lg</code></li>
      <li>🚫 Disabled state support</li>
      <li>🏷 Optional label</li>
      <li>🆔 Auto-generated or custom ID</li>
    </ul>

    <hr />

    <h2>📦 Props</h2>
    <ul>
      <li><b>modelValue</b> (Boolean, default: false) — Bound value</li>
      <li><b>label</b> (String) — Label text</li>
      <li><b>disabled</b> (Boolean, default: false) — Disable switch</li>
      <li><b>id</b> (String) — Custom input ID</li>
      <li><b>size</b> (String, default: md) — <code>sm</code> | <code>md</code> | <code>lg</code></li>
    </ul>

    <hr />

    <h2>📡 Emits</h2>
    <ul>
      <li><b>update:modelValue</b> — Emits Boolean when toggled</li>
    </ul>

    <hr />

    <h2>📌 Live Example: Basic</h2>

    <div style="max-width: 300px;">
      <switch-field v-model="enabled" label="Enable feature" />
      <p>Value: {{ enabled }}</p>
    </div>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;switch-field v-model="enabled" label="Enable feature" /&gt;

      data() {
        return {
          enabled: false
        }
      }
    </code></pre>

    <hr />

    <h2>📌 Live Example: Sizes</h2>

    <div class="d-flex flex-column gap-2" style="max-width: 300px;">
      <switch-field v-model="sizeSm" label="Small" size="sm" />
      <switch-field v-model="sizeMd" label="Medium" size="md" />
      <switch-field v-model="sizeLg" label="Large" size="lg" />
    </div>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;switch-field v-model="value" size="sm" /&gt;
      &lt;switch-field v-model="value" size="md" /&gt;
      &lt;switch-field v-model="value" size="lg" /&gt;
    </code></pre>

    <hr />

    <h2>📌 Live Example: Disabled</h2>

    <div style="max-width: 300px;">
      <switch-field v-model="disabledValue" label="Disabled switch" disabled />
    </div>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;switch-field v-model="value" disabled /&gt;
    </code></pre>

    <hr />

    <h2>🧠 Behavior</h2>
    <ul>
      <li>Toggle returns <code>true</code> or <code>false</code></li>
      <li>Fully controlled via <code>v-model</code></li>
      <li>Uses computed getter/setter for reactivity</li>
    </ul>

    <hr />

    <h2>✅ Notes</h2>
    <ul>
      <li>Generates unique <code>id</code> if not provided</li>
      <li>Uses Bootstrap switch classes</li>
      <li>Size is applied via CSS scaling</li>
    </ul>
  </div>
</template>

<script>
import SwitchField from "@/components/common/switch-field/SwitchField.vue";

export default {
  name: "SwitchFieldDocs",

  components: {
    SwitchField
  },

  data() {
    return {
      enabled: false,
      sizeSm: false,
      sizeMd: true,
      sizeLg: false,
      disabledValue: true
    };
  }
};
</script>
